'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { MdSearch } from 'react-icons/md'

import { Buble } from '@/components/atoms/buble'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/atoms/ui/tabs'
import { SearchArticle } from '@/components/organisms/search/search-article'
import Container from '@/components/templates/container/container'

import { algoliaClient } from '@/lib/utils/algolia'
import { formatCount } from '@/lib/utils/format-count'

interface SearchResultProps {
  appId: string
  searchKey: string
}

export const SearchResult = ({ appId, searchKey }: SearchResultProps) => {
  const params = useSearchParams()
  const [query, setQuery] = useState<string>(params.get('query') ?? '')
  const [results, setResults] = useState<Search[]>([])
  const router = useRouter()

  const TAB_LIST = ['Artikel']

  useEffect(() => {
    if (query.trim()) {
      router.push(`/search?query=${encodeURIComponent(query)}`)
      performSearch(query)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, router])

  const performSearch = async (searchQuery: string) => {
    try {
      const { hits } = await algoliaClient(appId, searchKey).search(searchQuery, {
        attributesToHighlight: ['title', 'category', 'shortDescription'],
        // page: 0,
        // hitsPerPage: 5,
        highlightPreTag: '-::',
        highlightPostTag: '::-'
      })
      setResults(hits as unknown as Search[])
    } catch (error) {
      console.error('Error performing search:', error)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      router.push(`/search?query=${encodeURIComponent(query)}`)
      performSearch(query)
    }
  }

  return (
    <div>
      <h1 className="sr-only">Halaman Pencarian</h1>
      <div className="relative z-0 w-full overflow-hidden h-60 bg-primary-blue">
        <Buble className="size-[400px] absolute -top-60 -right-20" />
      </div>

      <Container className="relative z-10 -mt-40 pb-40 min-h-screen max-w-[872px] space-y-11">
        <form className="flex items-center justify-between py-3 text-white border-b border-white" onSubmit={handleSearch}>
          <input
            defaultValue={query}
            onChange={(e) => setQuery(e.target.value)}
            type="text"
            className="w-full text-lg truncate bg-transparent outline-none lg:text-3xl"
            placeholder="Cari layanan, artikel, atau peraturan"
          />
          <button type="submit">
            <MdSearch size={30} />
          </button>
        </form>

        <div className="bg-white shadow-lg rounded-md p-4 lg:p-[30px] space-y-2">
          <span className="text-sm font-medium text-gray-600 lg:text-base">Hasil Pencarian:</span>
          <Tabs defaultValue="Artikel">
            <nav className="w-full overflow-x-auto border-b-2 scrollbar-hidden border-slate-300">
              <TabsList>
                {TAB_LIST.map((item) => {
                  let count = 0
                  if (item === 'Artikel') count = results.length

                  return (
                    <TabsTrigger key={item} value={item} className="relative flex items-center gap-1">
                      {item}
                      {count > 0 && (
                        <span className="flex items-center justify-center text-[8px] text-white bg-red-500 rounded-full size-5 truncate">
                          {formatCount(count)}
                        </span>
                      )}
                    </TabsTrigger>
                  )
                })}
              </TabsList>
            </nav>
            <TabsContent value="Artikel" className="w-full gap-6 py-6">
              {!results.length ? <p>Data tidak ditemukan...</p> : <SearchArticle data={results} />}
            </TabsContent>
          </Tabs>
        </div>
      </Container>
    </div>
  )
}
